import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import apiConfig from '../../config/apiConfig';
import './ManageDeeds.css';

const ManageDeeds = () => {
  const { user } = useContext(UserContext);
  const [deeds, setDeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('deedsList');
  const [editingDeedId, setEditingDeedId] = useState(null);
  
  // Check if user is admin (role 2 or higher)
  const isAdmin = user && user.role >= 2;

  // New deed state with default values
  const [newDeed, setNewDeed] = useState({
    level: 0,
    alignment: 0,
    name: '',
    content: '',
    success: '',
    failure: '',
    skills: {},
    reward: {
      gold: 0,
      experience: 0,
      alignment: 0,
      jailTime: 0
    }
  });

  // Add these state variables after your existing state declarations
  const [sortColumn, setSortColumn] = useState('level'); // Default sort by level
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingRow, setEditingRow] = useState(null);
  const [editingDeedData, setEditingDeedData] = useState(null);

  // Fetch deeds on component mount
  useEffect(() => {
    // If not admin, don't fetch data
    if (!isAdmin) return;
    
    fetchDeeds();
  }, [isAdmin]);

  // Function to fetch all deeds
  const fetchDeeds = async () => {
    try {
      setLoading(true);
      console.log('Fetching deeds from:', apiConfig.deeds);
      
      const response = await fetch(apiConfig.deeds, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch deeds: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Fetched deeds data:', data);
      setDeeds(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching deeds:', err);
      setError(`Failed to load deeds: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete deed function
  const deleteDeed = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deed?')) return;
    
    try {
      setLoading(true);
      const response = await fetch(apiConfig.deleteDeed(id), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete deed: ${response.status} ${response.statusText}`);
      }

      // Remove the deleted deed from state
      setDeeds(deeds.filter(deed => deed.id !== id));
      setSuccess('Deed deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting deed:', err);
      setError(`Failed to delete deed: ${err.message}`);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes for new/editing deed
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('reward.')) {
      const rewardField = name.split('.')[1];
      setNewDeed({
        ...newDeed,
        reward: {
          ...newDeed.reward,
          [rewardField]: parseInt(value, 10) || 0
        }
      });
    } else {
      setNewDeed({
        ...newDeed,
        [name]: name === 'level' || name === 'alignment' ? parseInt(value, 10) || 0 : value
      });
    }
  };

  // Set up form for editing an existing deed
  const editDeed = (deed) => {
    setEditingDeedId(deed.id);
    setNewDeed({
      level: deed.level,
      alignment: deed.alignment,
      name: deed.name,
      content: deed.content,
      success: deed.success || '',
      failure: deed.failure || '',
      skills: deed.skills || {},
      reward: deed.reward || {
        gold: 0,
        experience: 0,
        alignment: 0,
        jailTime: 0
      }
    });
    setActiveTab('addDeed');
  };

  // Reset the form
  const resetForm = () => {
    setNewDeed({
      level: 0,
      alignment: 0,
      name: '',
      content: '',
      success: '',
      failure: '',
      skills: {},
      reward: {
        gold: 0,
        experience: 0,
        alignment: 0,
        jailTime: 0
      }
    });
    setEditingDeedId(null);
  };

  // Create/update deed
  const saveDeed = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Determine if we're creating or updating a deed
      const isEditing = !!editingDeedId;
      console.log(`${isEditing ? 'Updating' : 'Creating'} deed with data:`, newDeed);
      
      // Use different endpoint and method based on whether we're editing or creating
      const url = isEditing ? apiConfig.updateDeed(editingDeedId) : apiConfig.deeds;
      const method = isEditing ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(isEditing ? newDeed : { ...newDeed })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} deed: ${response.status} ${response.statusText}`);
      }

      const savedDeed = await response.json();
      console.log('Server response:', savedDeed);
      
      if (isEditing) {
        setDeeds(deeds.map(deed => deed.id === editingDeedId ? savedDeed : deed));
        setSuccess('Deed updated successfully!');
      } else {
        setDeeds([...deeds, savedDeed]);
        setSuccess('New deed created successfully!');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
      // Reset form and switch to list view
      resetForm();
      setActiveTab('deedsList');
      
      // Refresh deeds list
      fetchDeeds();
    } catch (err) {
      console.error(`Error ${editingDeedId ? 'updating' : 'creating'} deed:`, err);
      setError(`Failed to ${editingDeedId ? 'update' : 'create'} deed: ${err.message}`);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Get alignment display name
  const getAlignmentName = (alignment) => {
    if (alignment > 0) return 'Good';
    if (alignment < 0) return 'Evil';
    return 'Neutral';
  };

  // Refresh the deeds list
  const refreshDeeds = () => {
    fetchDeeds();
  };

  // Add sorting function
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if already sorting by this column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Create sorted deeds array
  const sortedDeeds = React.useMemo(() => {
    return [...deeds].sort((a, b) => {
      if (!sortColumn) return 0;
      
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (sortColumn === 'level') {
        return (a.level - b.level) * direction;
      }
      
      if (sortColumn === 'alignment') {
        return (a.alignment - b.alignment) * direction;
      }
      
      return 0;
    });
  }, [deeds, sortColumn, sortDirection]);

  // Start inline editing
  const startInlineEdit = (deed) => {
    setEditingRow(deed.id);
    setEditingDeedData({...deed});
  };

  // Handle input changes for inline editing
  const handleInlineInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('reward.')) {
      const rewardField = name.split('.')[1];
      setEditingDeedData({
        ...editingDeedData,
        reward: {
          ...editingDeedData.reward,
          [rewardField]: parseInt(value, 10) || 0
        }
      });
    } else {
      setEditingDeedData({
        ...editingDeedData,
        [name]: name === 'level' || name === 'alignment' ? parseInt(value, 10) || 0 : value
      });
    }
  };

  // Save inline edits with PATCH request
  // Alternative implementation using the new apiConfig method
  const saveInlineEdit = async () => {
    try {
      setLoading(true);
      console.log('Saving deed with data:', editingDeedData);
      
      const response = await fetch(apiConfig.updateDeed(editingDeedData.id), {
        method: 'PATCH', 
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editingDeedData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update deed: ${response.status} ${response.statusText}`);
      }

      const savedDeed = await response.json();
      
      // Update deeds array with the updated deed
      setDeeds(deeds.map(deed => deed.id === editingDeedData.id ? savedDeed : deed));
      setSuccess('Deed updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
      // End editing mode
      cancelInlineEdit();
    } catch (err) {
      console.error('Error updating deed:', err);
      setError(`Failed to update deed: ${err.message}`);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Cancel inline editing
  const cancelInlineEdit = () => {
    setEditingRow(null);
    setEditingDeedData(null);
  };

  // If not admin, redirect to home page
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="page-content">
      <div className="manage-deeds-container">
        <h1>Manage Deeds</h1>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="admin-tabs">
          <button 
            className={activeTab === 'deedsList' ? 'active' : ''}
            onClick={() => {
              setActiveTab('deedsList');
              resetForm();
              refreshDeeds(); // Refresh the list when switching to list view
            }}
          >
            Deeds List
          </button>
          <button 
            className={activeTab === 'addDeed' ? 'active' : ''}
            onClick={() => {
              setActiveTab('addDeed');
              resetForm();
            }}
          >
            {editingDeedId ? 'Edit Deed' : 'Add New Deed'}
          </button>
        </div>
        
        {activeTab === 'deedsList' && (
          <div className="deeds-list-container">
            <div className="list-header">
              <h2>All Deeds</h2>
              <button 
                className="refresh-button"
                onClick={refreshDeeds}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh List'}
              </button>
            </div>
            
            {loading && deeds.length === 0 ? (
              <div className="loading">Loading deeds...</div>
            ) : deeds.length === 0 ? (
              <div className="no-deeds">No deeds found. Create your first deed!</div>
            ) : (
              <div className="deeds-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th 
                        className={`sortable ${sortColumn === 'level' ? 'sorted-' + sortDirection : ''}`}
                        onClick={() => handleSort('level')}
                      >
                        Level {sortColumn === 'level' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
                      </th>
                      <th 
                        className={`sortable ${sortColumn === 'alignment' ? 'sorted-' + sortDirection : ''}`}
                        onClick={() => handleSort('alignment')}
                      >
                        Alignment {sortColumn === 'alignment' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
                      </th>
                      <th>Gold</th>
                      <th>XP</th>
                      <th>Alignment Reward</th>
                      <th>Jail Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeeds.map(deed => (
                      <tr key={deed.id}>
                        {editingRow === deed.id ? (
                          // Inline edit mode
                          <>
                            <td>
                              <input 
                                type="text" 
                                name="name" 
                                value={editingDeedData.name} 
                                onChange={handleInlineInputChange}
                                required
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="level" 
                                min="0"
                                max="100"
                                value={editingDeedData.level} 
                                onChange={handleInlineInputChange}
                                required
                                style={{width: '50px'}}
                              />
                            </td>
                            <td>
                              <select
                                name="alignment"
                                value={editingDeedData.alignment}
                                onChange={handleInlineInputChange}
                                className={
                                  editingDeedData.alignment > 0 ? 'good' : 
                                  editingDeedData.alignment < 0 ? 'evil' : 'neutral'
                                }
                              >
                                <option value="1">Good (+1)</option>
                                <option value="0">Neutral (0)</option>
                                <option value="-1">Evil (-1)</option>
                              </select>
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="reward.gold" 
                                min="0"
                                value={editingDeedData.reward?.gold || 0} 
                                onChange={handleInlineInputChange}
                                style={{width: '60px'}}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="reward.experience" 
                                min="0"
                                value={editingDeedData.reward?.experience || 0} 
                                onChange={handleInlineInputChange}
                                style={{width: '60px'}}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="reward.alignment" 
                                value={editingDeedData.reward?.alignment || 0} 
                                onChange={handleInlineInputChange}
                                style={{width: '60px'}}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="reward.jailTime" 
                                min="0"
                                value={editingDeedData.reward?.jailTime || 0} 
                                onChange={handleInlineInputChange}
                                style={{width: '60px'}}
                              />
                            </td>
                            <td>
                              <button 
                                className="save-button" 
                                onClick={saveInlineEdit}
                                disabled={loading}
                              >
                                {loading ? 'Saving...' : 'Save'}
                              </button>
                              <button 
                                className="cancel-button" 
                                onClick={cancelInlineEdit}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          // Normal view mode
                          <>
                            <td>{deed.name}</td>
                            <td>{deed.level}</td>
                            <td className={
                              deed.alignment > 0 ? 'good' : 
                              deed.alignment < 0 ? 'evil' : 'neutral'
                            }>
                              {getAlignmentName(deed.alignment)}
                            </td>
                            <td>{deed.reward?.gold || 0}</td>
                            <td>{deed.reward?.experience || 0}</td>
                            <td className={
                              deed.reward?.alignment > 0 ? 'good' : 
                              deed.reward?.alignment < 0 ? 'evil' : 'neutral'
                            }>
                              {deed.reward?.alignment || 0}
                            </td>
                            <td>{deed.reward?.jailTime ? `${deed.reward.jailTime}s` : '-'}</td>
                            <td>
                              <div className="action-buttons">
                                <button 
                                  className="edit-button" 
                                  onClick={() => startInlineEdit(deed)}
                                  title="Quick edit basic properties"
                                >
                                  Edit
                                </button>
                                <button 
                                  className="full-edit-button" 
                                  onClick={() => editDeed(deed)}
                                  title="Edit all deed properties including description and messages"
                                >
                                  Full Edit
                                </button>
                                <button 
                                  className="delete-button" 
                                  onClick={() => deleteDeed(deed.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'addDeed' && (
          <div className="deed-form-container">
            <h2>{editingDeedId ? 'Edit Deed' : 'Create New Deed'}</h2>
            <form onSubmit={saveDeed}>
              <div className="form-section">
                <h3>Deed Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Deed Name:</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={newDeed.name} 
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="level">Level Requirement:</label>
                    <input 
                      type="number" 
                      id="level" 
                      name="level" 
                      min="0"
                      max="100"
                      value={newDeed.level} 
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="alignment">Alignment:</label>
                  <select
                    id="alignment"
                    name="alignment"
                    value={newDeed.alignment}
                    onChange={handleInputChange}
                    className={
                      newDeed.alignment > 0 ? 'alignment-good' : 
                      newDeed.alignment < 0 ? 'alignment-evil' : 'alignment-neutral'
                    }
                  >
                    <option value="1">Good (+1)</option>
                    <option value="0">Neutral (0)</option>
                    <option value="-1">Evil (-1)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="content">Description:</label>
                  <textarea 
                    id="content" 
                    name="content" 
                    value={newDeed.content} 
                    onChange={handleInputChange}
                    required
                    rows="4"
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="success">Success Message:</label>
                  <textarea 
                    id="success" 
                    name="success" 
                    value={newDeed.success} 
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Message shown to player upon deed completion"
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="failure">Failure Message:</label>
                  <textarea 
                    id="failure" 
                    name="failure" 
                    value={newDeed.failure} 
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Message shown to player upon deed failure"
                  ></textarea>
                </div>
              </div>
              
              <div className="form-section">
                <h3>Deed Rewards</h3>
                <div className="reward-grid">
                  <div className="form-group">
                    <label htmlFor="gold">Max Gold:</label>
                    <input 
                      type="number" 
                      id="gold" 
                      name="reward.gold" 
                      min="0"
                      value={newDeed.reward.gold} 
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="experience">Max Experience:</label>
                    <input 
                      type="number" 
                      id="experience" 
                      name="reward.experience" 
                      min="0"
                      value={newDeed.reward.experience} 
                      onChange={handleInputChange}
                    />
                  </div>
                
                  <div className="form-group">
                    <label htmlFor="alignment-points">Alignment Points:</label>
                    <input 
                      type="number" 
                      id="alignment-points" 
                      name="reward.alignment" 
                      value={newDeed.reward.alignment} 
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="jailTime">Jail Time (seconds):</label>
                    <input 
                      type="number" 
                      id="jailTime" 
                      name="reward.jailTime" 
                      min="0"
                      value={newDeed.reward.jailTime} 
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={() => {
                    setActiveTab('deedsList');
                    resetForm();
                    refreshDeeds(); // Refresh the list when canceling
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingDeedId ? 'Update Deed' : 'Create Deed'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageDeeds;
